import React from "react";
import { FiAlertCircle, FiCheck, FiX } from 'react-icons/fi';

function ProductCardsDispatch({ product, onRefund, productIndex }) {
  const { name, price, quantity, image, upc, substituteOrRefund, size, refunded, refundReason, refundApproved } = product;

  const handleRefundClick = (e) => {
    e.stopPropagation();
    if (onRefund) {
      onRefund(product, productIndex);
    }
  };

  return (
    <div className={`group relative bg-white border rounded-xl p-3 hover:shadow-md transition-all duration-200 ${
      refunded ? 'border-red-200 bg-red-50/30' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <div className="flex gap-3">
      {/* Image */}
        <div className="relative flex-shrink-0">
          <div className={`w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center ${refunded ? 'bg-red-50 opacity-60' : 'bg-gray-50'}`}>
            {image ? (
        <img
          src={image}
                alt={name}
                className={`object-contain w-full h-full ${refunded ? 'grayscale' : ''}`}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>
          
          {/* Quantity Badge */}
          {quantity > 1 && (
            <span className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm ${
              refunded ? 'bg-red-400' : 'bg-sky-500'
            }`}>
              {quantity}
            </span>
          )}

          {/* Refund Status Badge */}
          {refunded && (
            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
              <FiX className="w-3 h-3" />
            </span>
          )}
      </div>

      {/* Details */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <p className={`font-medium text-sm leading-tight line-clamp-2 transition-colors ${
              refunded ? 'text-gray-500 line-through' : 'text-gray-900 group-hover:text-sky-600'
            }`}>
              {name}
            </p>
            
            {size && (
              <p className="text-xs text-gray-500 mt-0.5">{size}</p>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Price */}
            <span className={`font-semibold text-sm ${refunded ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
              ${parseFloat(price || 0).toFixed(2)}
            </span>

            {/* Quantity (when 1) */}
            {quantity === 1 && (
              <span className="text-xs text-gray-500">
                Qty: {quantity}
              </span>
            )}

            {/* Substitute/Refund Customer Preference Badge */}
          {substituteOrRefund && (
              <span className={`
                px-2 py-0.5 rounded-full text-xs font-semibold uppercase
                ${substituteOrRefund.toLowerCase() === 'substitute' 
                  ? 'bg-amber-100 text-amber-700' 
                  : 'bg-coral-100 text-coral-700'
                }
              `}>
                {substituteOrRefund}
              </span>
            )}

            {/* Refunded Badge */}
            {refunded && (
              <span className={`
                px-2 py-0.5 rounded-full text-xs font-semibold uppercase flex items-center gap-1
                ${refundApproved === true ? 'bg-emerald-100 text-emerald-700' : 
                  refundApproved === false ? 'bg-red-100 text-red-700' :
                  'bg-orange-100 text-orange-700'}
              `}>
                {refundApproved === true ? (
                  <><FiCheck className="w-3 h-3" /> Approved</>
                ) : refundApproved === false ? (
                  <><FiX className="w-3 h-3" /> Rejected</>
                ) : (
                  <><FiAlertCircle className="w-3 h-3" /> Pending</>
                )}
              </span>
            )}
          </div>

          {/* Refund Reason */}
          {refunded && refundReason && (
            <p className="text-xs text-red-600 mt-1 italic">
              Refund: {refundReason}
            </p>
          )}
        </div>
      </div>

      {/* UPC (shown on hover) */}
      {upc && (
        <div className="mt-2 pt-2 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="text-xs text-gray-400 font-mono">
            UPC: {upc}
          </p>
        </div>
      )}

      {/* Refund Button - only show if not already refunded and callback exists */}
      {!refunded && onRefund && (
        <button
          onClick={handleRefundClick}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100"
          title="Mark for refund"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Undo Refund Button - show if refunded and not yet approved */}
      {refunded && !refundApproved && onRefund && (
        <button
          onClick={handleRefundClick}
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
          title="Undo refund"
        >
          <FiCheck className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default ProductCardsDispatch;
